import React from "react";
import { Helmet } from "react-helmet";

const SITE_URL = "https://nihalparmarportfolio.web.app";
const SITE_NAME = "Nihal Parmar - Software Engineer | Backend Developer Portfolio";
const DEFAULT_DESCRIPTION =
  "Portfolio of Nihal Parmar, a Software Engineer with 3+ years of experience building scalable backend systems, distributed architectures, cloud-native microservices, and LLM-driven AI pipelines. Specializing in Node.js, React, AWS, and Kubernetes.";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const TWITTER_HANDLE = "@itznihal";

const SEO = ({
  title = SITE_NAME,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  path = "",
  type = "website",
  keywords = "Nihal Parmar, Software Engineer, Backend Developer, GenAI Engineer, LLM, RAG, Agentic RAG, Node.js, TypeScript, React, AWS, Kubernetes, PostgreSQL, MongoDB, Microservices, Distributed Systems, Ontology, Portfolio, India",
}) => {
  const url = path ? `${SITE_URL}${path}` : SITE_URL;
  const fullTitle = path ? `${title} | ${SITE_NAME}` : title;

  // Structured Data - Person Schema
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Nihal Parmar",
    jobTitle: "Software Engineer",
    url: SITE_URL,
    image: DEFAULT_IMAGE,
    sameAs: [
      "https://github.com/itznihal",
      "https://www.linkedin.com/in/nihal-parmar-2924411b7/",
      "https://itznihal.medium.com/",
      "https://www.instagram.com/nihalparmar_22",
    ],
    email: "itznihal143@gmail.com",
    telephone: "+918980452333",
    address: {
      "@type": "PostalAddress",
      addressCountry: "IN",
    },
    description: DEFAULT_DESCRIPTION,
    knowsAbout: [
      "Software Engineering",
      "Backend Development",
      "Full Stack Development",
      "Node.js",
      "React",
      "AWS",
      "Kubernetes",
      "Microservices",
      "Distributed Systems",
      "Database Design",
      "Cloud Computing",
      "Large Language Models",
      "Machine Learning",
      "Retrieval Augmented Generation",
      "TypeScript",
      "PostgreSQL",
      "Ontology",
    ],
    worksFor: {
      "@type": "Organization",
      name: "Troopr Labs",
      jobTitle: "Software Engineer",
    },
  };

  // Structured Data - Website Schema (no SearchAction — site has no search UI)
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    inLanguage: "en",
    publisher: {
      "@type": "Person",
      name: "Nihal Parmar",
      url: SITE_URL,
    },
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Nihal Parmar" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="language" content="English" />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:image:alt" content="Nihal Parmar - Software Engineer Portfolio" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content="Nihal Parmar - Software Engineer Portfolio" />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:site" content={TWITTER_HANDLE} />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#ffb727" />
      <meta name="msapplication-TileColor" content="#ffb727" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Nihal Parmar" />

      {/* Favicon Links - Multiple sizes for better compatibility */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="96x96" href="/favicon-96x96.png" />
      <link rel="icon" type="image/png" sizes="192x192" href="/android-chrome-192x192.png" />
      <link rel="icon" type="image/png" sizes="512x512" href="/android-chrome-512x512.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/manifest.json" />

      {/* Preconnect for Performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://api.emailjs.com" />
      <link rel="dns-prefetch" href="https://api.rss2json.com" />

      {/* Structured Data - JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
    </Helmet>
  );
};

export default SEO;

